import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Plus } from "lucide-react";
import { mutate } from "swr";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  CATALOGUES_CACHE_KEY,
  CatalogueService,
  NO_PROCESSING_FEATURES,
  type CatalogueFeature,
  type ProcessingFeatureId,
  type ProcessingFeatures,
  isProcessingFeatureId,
} from "@/services/catalogue.service";
import { CatalogueTemplateService } from "@/services/catalogue-template.service";
import {
  ModerationRulesetService,
  type ModerationRuleset,
} from "@/services/moderation-ruleset.service";
import { AlertMessage } from "@/components/ui/alert-message";
import { LanguageSelector } from "@/components/catalog/language-selector";
import { useOrganization } from "@/context/OrganizationContext";
import {
  DEFAULT_CATALOGUE_LANGUAGES,
  type LanguageCode,
} from "@/constants/languages";
import type {
  PublishedCategoryTemplate,
} from "@/services/catalogue-template.service";

interface CreateCatalogueButtonProps {
  onCatalogueCreated?: () => void;
}

const NO_TEMPLATE_VALUE = "none";
const NO_RULESET_VALUE = "none";
const TEMPLATE_LOAD_WARNING =
  "Configuration options could not be loaded. You can still create a catalogue without them.";
const RULESET_LOAD_WARNING =
  "Moderation rulesets could not be loaded. You can still create a catalogue and assign one later.";

function assignmentFailureMessage(cause: unknown): string {
  const reason = cause instanceof Error ? cause.message : null;
  return [
    "The catalogue was created, but the moderation ruleset could not be assigned.",
    reason,
    "You can assign it from the catalogue's Moderation tab.",
  ]
    .filter(Boolean)
    .join(" ");
}

export function CreateCatalogueButton({ onCatalogueCreated }: CreateCatalogueButtonProps) {
  const { can } = useOrganization();
  const canManageCatalogue = can("catalog");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [features, setFeatures] = useState<CatalogueFeature[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<ProcessingFeatures>({
    ...NO_PROCESSING_FEATURES,
  });
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);
  const [featureLoadError, setFeatureLoadError] = useState<string | null>(null);
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageCode[]>(
    DEFAULT_CATALOGUE_LANGUAGES
  );
  const [categoryTemplates, setCategoryTemplates] = useState<PublishedCategoryTemplate[]>([]);
  const [selectedCategoryTemplateId, setSelectedCategoryTemplateId] = useState(NO_TEMPLATE_VALUE);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateWarning, setTemplateWarning] = useState<string | null>(null);
  const [rulesets, setRulesets] = useState<ModerationRuleset[]>([]);
  const [selectedRulesetId, setSelectedRulesetId] = useState(NO_RULESET_VALUE);
  const [isLoadingRulesets, setIsLoadingRulesets] = useState(false);
  const [rulesetWarning, setRulesetWarning] = useState<string | null>(null);
  // Set when the catalogue was created but its ruleset assignment failed. The
  // catalogue exists at that point, so the form must not be submitted again.
  const [unassignedCatalogueId, setUnassignedCatalogueId] = useState<string | null>(null);
  const [isRetryingAssignment, setIsRetryingAssignment] = useState(false);

  const knownFeatures = useMemo(
    () => features.filter((feature): feature is CatalogueFeature & { id: ProcessingFeatureId } =>
      isProcessingFeatureId(feature.id)
    ),
    [features]
  );
  const hasSelectedFeature = Object.values(selectedFeatures).some(Boolean);
  const canCreate = canManageCatalogue && !isLoadingFeatures && !featureLoadError && hasSelectedFeature;

  useEffect(() => {
    if (!isCreateDialogOpen) return;
    let isMounted = true;
    setIsLoadingFeatures(true);
    setFeatureLoadError(null);
    setError(null);
    setTemplateWarning(null);
    setRulesetWarning(null);
    setUnassignedCatalogueId(null);
    setSelectedFeatures({ ...NO_PROCESSING_FEATURES });
    setSelectedCategoryTemplateId(NO_TEMPLATE_VALUE);
    setSelectedRulesetId(NO_RULESET_VALUE);
    setSelectedLanguages(DEFAULT_CATALOGUE_LANGUAGES);

    void CatalogueService.getFeatures()
      .then((availableFeatures) => {
        if (!isMounted) return;
        const entitled = { ...NO_PROCESSING_FEATURES };
        for (const feature of availableFeatures) {
          if (isProcessingFeatureId(feature.id) && feature.id !== "roomVisualiserDataExtraction") {
            entitled[feature.id] = feature.allowed;
          }
        }
        setFeatures(availableFeatures);
        setSelectedFeatures(entitled);
      })
      .catch((loadError: unknown) => {
        if (!isMounted) return;
        setFeatures([]);
        setSelectedFeatures({ ...NO_PROCESSING_FEATURES });
        setFeatureLoadError(
          loadError instanceof Error ? loadError.message : "Processing features could not be loaded"
        );
      })
      .finally(() => {
        if (isMounted) setIsLoadingFeatures(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isCreateDialogOpen]);

  useEffect(() => {
    if (!isCreateDialogOpen || !selectedFeatures.aiProcessing) {
      setIsLoadingTemplates(false);
      setTemplateWarning(null);
      return;
    }

    let isMounted = true;
    setIsLoadingTemplates(true);
    setTemplateWarning(null);

    void CatalogueTemplateService.listPublishedCategoryTemplates()
      .then((templates) => {
        if (!isMounted) return;
        const expected = [...selectedLanguages].sort().join(",");
        const compatible = templates.filter((template) =>
          template.status === "published"
          && [...template.languages].sort().join(",") === expected
        );
        setCategoryTemplates(compatible);
        setSelectedCategoryTemplateId((current) =>
          current && compatible.some((template) => template.id === current) ? current : "",
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setCategoryTemplates([]);
        setTemplateWarning(TEMPLATE_LOAD_WARNING);
      })
      .finally(() => {
        if (isMounted) setIsLoadingTemplates(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isCreateDialogOpen, selectedFeatures.aiProcessing, selectedLanguages]);

  useEffect(() => {
    if (unassignedCatalogueId) return;
    if (!isCreateDialogOpen || !selectedFeatures.moderation) {
      setIsLoadingRulesets(false);
      setRulesetWarning(null);
      return;
    }

    let isMounted = true;
    setIsLoadingRulesets(true);
    setRulesetWarning(null);

    void ModerationRulesetService.listRulesets()
      .then((allRulesets) => {
        if (!isMounted) return;
        // Archived rulesets stay listed for history but cannot be assigned.
        setRulesets(allRulesets.filter((ruleset) => !ruleset.archivedAt));
      })
      .catch(() => {
        if (!isMounted) return;
        setRulesets([]);
        setRulesetWarning(RULESET_LOAD_WARNING);
      })
      .finally(() => {
        if (isMounted) setIsLoadingRulesets(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isCreateDialogOpen, selectedFeatures.moderation, unassignedCatalogueId]);

  const toggleFeature = (id: ProcessingFeatureId, enabled: boolean) => {
    const definition = knownFeatures.find((feature) => feature.id === id);
    if (!canManageCatalogue || !definition?.allowed) return;
    setSelectedFeatures((current) => ({ ...current, [id]: enabled }));
    if (!enabled && id === "aiProcessing") {
      setSelectedCategoryTemplateId(NO_TEMPLATE_VALUE);
    }
    if (!enabled && id === "moderation") {
      setSelectedRulesetId(NO_RULESET_VALUE);
    }
    setError(null);
  };

  const closeAfterCreate = () => {
    setIsCreateDialogOpen(false);
    setNewName("");
    setNewDescription("");
    setSelectedCategoryTemplateId(NO_TEMPLATE_VALUE);
    setSelectedRulesetId(NO_RULESET_VALUE);
    setSelectedLanguages(DEFAULT_CATALOGUE_LANGUAGES);
    setTemplateWarning(null);
    setRulesetWarning(null);
    setUnassignedCatalogueId(null);
  };

  const handleCreateCatalogue = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newName.trim()) return;
    if (!canCreate) {
      setError(featureLoadError ?? "Select at least one processing feature available on your plan.");
      return;
    }

    setIsCreating(true);
    setError(null);
    setRulesetWarning(null);
    try {
      const catalogue = await CatalogueService.create(
        newName.trim(),
        newDescription.trim() || undefined,
        {
          processingFeatures: selectedFeatures,
          languages: selectedLanguages,
          ...(selectedFeatures.aiProcessing && selectedCategoryTemplateId !== NO_TEMPLATE_VALUE
            ? { category_template_id: selectedCategoryTemplateId }
            : {}),
        }
      );

      // The ruleset lives in moderation-api, so it is a second write against a
      // catalogue that already exists. A failure here is not a failed creation.
      const rulesetId =
        selectedFeatures.moderation && selectedRulesetId !== NO_RULESET_VALUE
          ? selectedRulesetId
          : null;
      if (rulesetId) {
        try {
          await ModerationRulesetService.setCatalogueAssignment(catalogue.id, rulesetId);
        } catch (assignError: unknown) {
          setUnassignedCatalogueId(catalogue.id);
          setRulesetWarning(assignmentFailureMessage(assignError));
          await mutate(CATALOGUES_CACHE_KEY);
          onCatalogueCreated?.();
          return;
        }
      }

      closeAfterCreate();
      await mutate(CATALOGUES_CACHE_KEY);
      onCatalogueCreated?.();
    } catch (createError: unknown) {
      setError(createError instanceof Error ? createError.message : "Failed to create catalogue");
      void mutate(CATALOGUES_CACHE_KEY).catch(() => undefined);
    } finally {
      setIsCreating(false);
    }
  };

  const retryAssignment = async () => {
    if (!unassignedCatalogueId || selectedRulesetId === NO_RULESET_VALUE) return;
    setIsRetryingAssignment(true);
    try {
      await ModerationRulesetService.setCatalogueAssignment(
        unassignedCatalogueId,
        selectedRulesetId
      );
      closeAfterCreate();
    } catch (assignError: unknown) {
      setRulesetWarning(assignmentFailureMessage(assignError));
    } finally {
      setIsRetryingAssignment(false);
    }
  };

  return (
    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
      <DialogTrigger asChild>
        <Button disabled={!canManageCatalogue} className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600">
          <Plus className="mr-2 h-4 w-4" /> Create Catalogue
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create New Catalogue</DialogTitle>
          <DialogDescription>
            Choose the processing features this catalogue needs. Room Visualiser Data Extraction can also be changed later in Settings.
          </DialogDescription>
        </DialogHeader>

        <AlertMessage message={error ?? featureLoadError} type="error" />
        {[templateWarning, rulesetWarning].filter(Boolean).map((warning) => (
          <div
            key={warning}
            className="flex items-start rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700"
          >
            <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
            {warning}
          </div>
        ))}

        <form onSubmit={handleCreateCatalogue} className="space-y-6">
          <fieldset
            className="space-y-3"
            disabled={!canManageCatalogue || isCreating || isLoadingFeatures || unassignedCatalogueId !== null}
          >
            <legend className="text-sm font-medium">Processing features</legend>
            {isLoadingFeatures ? (
              <p className="text-sm text-muted-foreground">Loading features available on your plan...</p>
            ) : (
              <div className="space-y-2">
                {knownFeatures.map((feature) => (
                  <div key={feature.id} className="flex items-start justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label htmlFor={`catalogue-feature-${feature.id}`} className="font-medium">
                        {feature.label}
                      </Label>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {feature.description}
                      </p>
                      {!feature.allowed ? (
                        <span className="mt-1 inline-block text-xs font-medium text-muted-foreground">
                          Not in your plan
                        </span>
                      ) : null}
                    </div>
                    <Switch
                      id={`catalogue-feature-${feature.id}`}
                      checked={selectedFeatures[feature.id]}
                      disabled={!feature.allowed}
                      onCheckedChange={(checked) => toggleFeature(feature.id, checked)}
                      aria-label={`${feature.label} processing`}
                    />
                  </div>
                ))}
                {!featureLoadError && knownFeatures.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No processing features are available.</p>
                ) : null}
              </div>
            )}
            {!hasSelectedFeature && !isLoadingFeatures ? (
              <p className="text-sm text-red-600">Select at least one available processing feature.</p>
            ) : null}
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="e.g. Summer Collection"
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="description">Description</Label>
              <span className="text-xs text-muted-foreground">Optional</span>
            </div>
            <Textarea
              id="description"
              value={newDescription}
              onChange={(event) => setNewDescription(event.target.value)}
              placeholder="Add context about the products in this catalogue"
              rows={3}
            />
          </div>

          {selectedFeatures.aiProcessing ? (
            <LanguageSelector
              value={selectedLanguages}
              onChange={setSelectedLanguages}
              idPrefix="create-catalogue-language"
            />
          ) : null}

          {selectedFeatures.aiProcessing ? (
            <div className="space-y-2">
              <Label htmlFor="category-template">Categories &amp; attributes</Label>
              <Select
                value={selectedCategoryTemplateId}
                onValueChange={setSelectedCategoryTemplateId}
                disabled={isLoadingTemplates}
              >
                <SelectTrigger id="category-template"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TEMPLATE_VALUE}>None</SelectItem>
                  {categoryTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.version})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {selectedFeatures.moderation ? (
            <div className="space-y-2">
              <Label htmlFor="moderation-ruleset">Moderation rules</Label>
              <Select
                value={selectedRulesetId}
                onValueChange={setSelectedRulesetId}
                disabled={isLoadingRulesets || unassignedCatalogueId !== null}
              >
                <SelectTrigger id="moderation-ruleset"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_RULESET_VALUE}>None</SelectItem>
                  {rulesets.map((ruleset) => (
                    <SelectItem key={ruleset.id} value={ruleset.id}>
                      {ruleset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {isLoadingTemplates ? (
            <p className="text-sm text-muted-foreground">Loading template options...</p>
          ) : null}

          {isLoadingRulesets ? (
            <p className="text-sm text-muted-foreground">Loading moderation rulesets...</p>
          ) : null}

          <DialogFooter className="border-t pt-4">
            {unassignedCatalogueId ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeAfterCreate}
                  disabled={isRetryingAssignment}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={() => void retryAssignment()}
                  disabled={isRetryingAssignment}
                >
                  {isRetryingAssignment ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Assigning...</>
                  ) : "Assign ruleset again"}
                </Button>
              </>
            ) : (
              <>
                <DialogClose asChild>
                  <Button type="button" variant="outline" disabled={isCreating}>Cancel</Button>
                </DialogClose>
                <Button type="submit" disabled={isCreating || !canCreate || !newName.trim()}>
                  {isCreating ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</>
                  ) : "Create catalogue"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
